// KPI dashboard wired su /metrics/summary + /metrics/events + /metrics/goals.
// Refit aligned con mockup kpi-matrix.html:
// - Header rich + segmented period (Mese / Trim / YTD)
// - Filterbar: search nome + segmented viz mode (Valori / % target / Δ vs prev)
// - Matrix: row consulente con avatar + role + badges, header col con ✎ edit
//   target globale, cell con sparkline mini + numero + progress
// - Composite col client-side (avg progress_pct)
// - Footer team_total + team_avg
// - Drill modal eventi + GoalEditModal + Snapshot PDF (window.print)

import { useMemo, useState } from 'react'
import { createFileRoute, redirect } from '@tanstack/react-router'
import { hasTokens } from '../lib/auth'
import {
  useCreateMetricGoal,
  useMetricsEvents,
  useMetricsSummary,
  usePatchMetricGoal,
} from '../hooks/useMetrics'
import { useMe } from '../hooks/useAuth'
import { CardSkeleton } from '../components/Skeleton'
import type {
  MetricCell,
  MetricsSummaryResponse,
  UserMetricsRow,
} from '../lib/api'

export const Route = createFileRoute('/kpi')({
  beforeLoad: () => {
    if (!hasTokens()) {
      throw redirect({ to: '/login' })
    }
  },
  component: KpiPage,
})

const METRIC_LABELS: Record<string, string> = {
  sopralluoghi: 'Sopralluoghi',
  partecipazioni: 'Partecipazioni',
  aste_vinte: 'Aste vinte',
  app_presenza: 'App presenza',
  app_online: 'App online',
}

const METRIC_SHORT: Record<string, string> = {
  sopralluoghi: 'Sopr.',
  partecipazioni: 'Partec.',
  aste_vinte: 'Aggiud.',
  app_presenza: 'Pres.',
  app_online: 'Online',
}

type VizMode = 'valori' | 'percento' | 'delta'
type PeriodMode = 'mese' | 'trim' | 'ytd'

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('it-IT', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function currentMonthId(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function currentTrimId(): string {
  // Backend non supporta Q1/Q2: usiamo period_id del primo mese del
  // trimestre. Aggregato lato FE non fattibile senza endpoint dedicato.
  // Per ora mese fallback con etichetta diversa.
  const d = new Date()
  const trimStartMonth = Math.floor(d.getMonth() / 3) * 3
  return `${d.getFullYear()}-${String(trimStartMonth + 1).padStart(2, '0')}`
}

function previousMonthId(id: string): string {
  const [y, m] = id.split('-').map((s) => parseInt(s, 10))
  const d = new Date(y, m - 2, 1)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

function periodLabel(id: string): string {
  const [y, m] = id.split('-').map((s) => parseInt(s, 10))
  const d = new Date(y, m - 1, 1)
  return d
    .toLocaleDateString('it-IT', { month: 'long', year: 'numeric' })
    .replace(/^\w/, (c) => c.toUpperCase())
}

function buildMonthOptions(): Array<{ id: string; label: string }> {
  const out: Array<{ id: string; label: string }> = []
  const now = new Date()
  for (let i = 0; i < 12; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
    const id = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    out.push({ id, label: periodLabel(id) })
  }
  return out
}

// Composite score = avg progress_pct su metriche con target definito.
// Returns null se nessuna metric ha target.
function computeComposite(metrics: Record<string, MetricCell>): number | null {
  const pcts: number[] = []
  for (const k in metrics) {
    const c = metrics[k]
    if (c.target != null && c.target > 0) {
      pcts.push(Math.min(100, c.progress_pct ?? 0))
    }
  }
  if (pcts.length === 0) return null
  return Math.round(pcts.reduce((a, b) => a + b, 0) / pcts.length)
}

function KpiPage() {
  const { data: me } = useMe()
  const monthOptions = useMemo(() => buildMonthOptions(), [])
  const [periodMode, setPeriodMode] = useState<PeriodMode>('mese')
  const [periodId, setPeriodId] = useState<string>(currentMonthId())
  const [vizMode, setVizMode] = useState<VizMode>('valori')
  const [search, setSearch] = useState('')

  const [drillCell, setDrillCell] = useState<{
    user_id: string
    user_name: string
    metric_key: string
  } | null>(null)
  const [editingGoal, setEditingGoal] = useState<{
    user_id: string | null  // null = target globale per metric (header col)
    user_name: string
    metric_key: string
    target: number | null
    goal_id: string | null
  } | null>(null)

  const { data, isLoading, isError } = useMetricsSummary(periodId)
  const prevPeriodId = useMemo(() => previousMonthId(periodId), [periodId])
  // Fetch periodo precedente solo se viz mode = delta (evita extra query).
  const { data: prevData } = useMetricsSummary(
    vizMode === 'delta' ? prevPeriodId : undefined,
  )
  const canEditGoals = !!data?.targets_editable

  // Filter rows by search.
  const filteredRows = useMemo(() => {
    if (!data) return []
    const q = search.trim().toLowerCase()
    if (!q) return data.rows
    return data.rows.filter((r) => r.user_name.toLowerCase().includes(q))
  }, [data, search])

  function exportCsv() {
    if (!data) return
    const rows: string[][] = []
    const header = [
      'Consulente',
      ...data.metric_keys.map((k) => METRIC_LABELS[k] ?? k),
      'Composite',
    ]
    rows.push(header)
    for (const r of data.rows) {
      const line = [r.user_name]
      for (const mk of data.metric_keys) {
        const cell = r.metrics[mk]
        if (!cell) {
          line.push('')
          continue
        }
        const tgt = cell.target != null ? `/${cell.target}` : ''
        line.push(`${cell.actual}${tgt}`)
      }
      const comp = computeComposite(r.metrics)
      line.push(comp != null ? `${comp}%` : '')
      rows.push(line)
    }
    if (data.team_total) {
      const line = ['Team totale']
      for (const mk of data.metric_keys) {
        const c = data.team_total.metrics[mk]
        line.push(c ? String(c.actual) : '')
      }
      line.push('')
      rows.push(line)
    }
    const csv = rows
      .map((r) =>
        r
          .map((cell) => {
            const s = String(cell ?? '')
            return s.includes(',') || s.includes('"') || s.includes('\n')
              ? `"${s.replace(/"/g, '""')}"`
              : s
          })
          .join(','),
      )
      .join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `kpi-${periodId}.csv`
    a.click()
    URL.revokeObjectURL(url)
  }

  function snapshotPdf() {
    window.print()
  }

  void me

  return (
    <>
      {/* HEADER */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--sp-3)',
          marginBottom: 'var(--sp-3)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ flex: 1, minWidth: 280 }}>
          <div
            className="muted"
            style={{
              fontSize: 'var(--fs-10)',
              textTransform: 'uppercase',
              letterSpacing: 'var(--tracking-caps)',
              marginBottom: 4,
            }}
          >
            Operativo · <span style={{ color: 'var(--ink-2)' }}>KPI</span>
          </div>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--sp-3)',
              flexWrap: 'wrap',
            }}
          >
            <h1
              style={{
                margin: 0,
                fontSize: 'var(--fs-20)',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              KPI Team
            </h1>
            {data && (
              <span className="muted" style={{ fontSize: 'var(--fs-12)' }}>
                Performance individuale · periodo{' '}
                <b style={{ color: 'var(--ink-1)' }}>
                  {data.period?.label ?? periodLabel(periodId)}
                </b>{' '}
                · <b>{data.rows.length}</b> consulenti ·{' '}
                <b>{data.metric_keys.length}</b> metriche
              </span>
            )}
          </div>
        </div>

        {/* Page actions */}
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          <SegmentedPeriod
            value={periodMode}
            onChange={(m) => {
              setPeriodMode(m)
              if (m === 'mese') setPeriodId(currentMonthId())
              else if (m === 'trim') setPeriodId(currentTrimId())
              else if (m === 'ytd') setPeriodId(`${new Date().getFullYear()}-01`)
            }}
          />
          {periodMode === 'mese' && (
            <select
              value={periodId}
              onChange={(e) => setPeriodId(e.target.value)}
              style={{
                padding: '6px 10px',
                fontSize: 'var(--fs-12)',
                borderRadius: 'var(--r-md)',
                border: '1px solid var(--line)',
                background: 'var(--surface)',
                cursor: 'pointer',
              }}
            >
              {monthOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          )}
          <button
            type="button"
            className="btn sm"
            onClick={exportCsv}
            disabled={!data}
            title="Esporta matrice CSV"
          >
            ⬇ Esporta CSV
          </button>
          <button
            type="button"
            className="btn sm"
            onClick={snapshotPdf}
            disabled={!data}
            title="Snapshot PDF (stampa)"
          >
            📄 Snapshot PDF
          </button>
        </div>
      </div>

      {/* FILTERBAR */}
      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-2)',
          alignItems: 'center',
          marginBottom: 'var(--sp-3)',
          padding: 'var(--sp-2) var(--sp-3)',
          background: 'var(--bg-2)',
          borderRadius: 'var(--r-md)',
          flexWrap: 'wrap',
        }}
      >
        <input
          type="search"
          placeholder="Cerca consulente…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{
            flex: '0 1 240px',
            padding: '4px 10px',
            fontSize: 'var(--fs-12)',
            borderRadius: 'var(--r-sm)',
            border: '1px solid var(--line)',
            background: 'var(--surface)',
          }}
        />
        <span
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            textTransform: 'uppercase',
            letterSpacing: 'var(--tracking-caps)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          Visualizza
        </span>
        <SegmentedViz value={vizMode} onChange={setVizMode} />
        <span style={{ flex: 1 }} />
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
          {filteredRows.length}/{data?.rows.length ?? 0} consulenti · click
          cella per dettagli · ✎ header per editare target
        </span>
      </div>

      {/* Body */}
      {isLoading && <CardSkeleton />}
      {isError && (
        <div className="error" style={{ padding: 'var(--sp-4)' }}>
          Errore caricamento metriche.
        </div>
      )}
      {data && !isLoading && (
        <KpiMatrix
          data={data}
          prevData={prevData ?? null}
          rows={filteredRows}
          vizMode={vizMode}
          canEditGoals={canEditGoals}
          onDrill={(user, mk) =>
            setDrillCell({
              user_id: user.user_id,
              user_name: user.user_name,
              metric_key: mk,
            })
          }
          onEditGoal={(user, mk, cell) =>
            setEditingGoal({
              user_id: user.user_id,
              user_name: user.user_name,
              metric_key: mk,
              target: cell?.target ?? null,
              goal_id: cell?.goal_id ?? null,
            })
          }
          onEditGlobalGoal={(mk) =>
            setEditingGoal({
              user_id: null,
              user_name: 'Tutti i consulenti',
              metric_key: mk,
              target: null,
              goal_id: null,
            })
          }
        />
      )}

      {drillCell && (
        <DrillModal
          userId={drillCell.user_id}
          userName={drillCell.user_name}
          metricKey={drillCell.metric_key}
          periodId={periodId}
          onClose={() => setDrillCell(null)}
        />
      )}

      {editingGoal && (
        <GoalEditModal
          userId={editingGoal.user_id}
          userName={editingGoal.user_name}
          metricKey={editingGoal.metric_key}
          periodId={periodId}
          currentTarget={editingGoal.target}
          goalId={editingGoal.goal_id}
          rows={data?.rows ?? []}
          onClose={() => setEditingGoal(null)}
        />
      )}
    </>
  )
}

// ============================================================================
// Segmented controls
// ============================================================================

function SegmentedPeriod({
  value,
  onChange,
}: {
  value: PeriodMode
  onChange: (v: PeriodMode) => void
}) {
  const opts: Array<{ key: PeriodMode; label: string }> = [
    { key: 'mese', label: 'Mese' },
    { key: 'trim', label: 'Trim.' },
    { key: 'ytd', label: 'YTD' },
  ]
  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: 2,
        gap: 2,
      }}
    >
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          style={{
            background: value === o.key ? 'var(--ink-1)' : 'transparent',
            color: value === o.key ? 'var(--surface)' : 'var(--ink-2)',
            border: 0,
            padding: '4px 12px',
            borderRadius: 'var(--r-sm)',
            fontSize: 'var(--fs-12)',
            fontWeight:
              value === o.key ? 'var(--fw-semibold)' : 'var(--fw-medium)',
            cursor: 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

function SegmentedViz({
  value,
  onChange,
}: {
  value: VizMode
  onChange: (v: VizMode) => void
}) {
  const opts: Array<{ key: VizMode; label: string }> = [
    { key: 'valori', label: 'Valori' },
    { key: 'percento', label: '% target' },
    { key: 'delta', label: 'Δ vs prev' },
  ]
  return (
    <div
      style={{
        display: 'inline-flex',
        background: 'var(--surface)',
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        padding: 2,
        gap: 2,
      }}
    >
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          style={{
            background:
              value === o.key ? 'var(--accent-soft)' : 'transparent',
            color: value === o.key ? 'var(--accent-ink)' : 'var(--ink-2)',
            border: 0,
            padding: '4px 10px',
            borderRadius: 'var(--r-sm)',
            fontSize: 'var(--fs-11)',
            fontWeight:
              value === o.key ? 'var(--fw-semibold)' : 'var(--fw-medium)',
            cursor: 'pointer',
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

// ============================================================================
// Matrix
// ============================================================================

function KpiMatrix({
  data,
  prevData,
  rows,
  vizMode,
  canEditGoals,
  onDrill,
  onEditGoal,
  onEditGlobalGoal,
}: {
  data: MetricsSummaryResponse
  prevData: MetricsSummaryResponse | null
  rows: UserMetricsRow[]
  vizMode: VizMode
  canEditGoals: boolean
  onDrill: (user: UserMetricsRow, metricKey: string) => void
  onEditGoal: (
    user: UserMetricsRow,
    metricKey: string,
    cell: MetricCell | undefined,
  ) => void
  onEditGlobalGoal: (metricKey: string) => void
}) {
  return (
    <div
      style={{
        border: '1px solid var(--line)',
        borderRadius: 'var(--r-md)',
        background: 'var(--surface)',
        overflow: 'auto',
      }}
    >
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: 'var(--fs-12)',
        }}
      >
        <thead>
          <tr style={{ background: 'var(--bg-2)' }}>
            <Th left>Consulente</Th>
            {data.metric_keys.map((mk) => (
              <ThMetric
                key={mk}
                label={METRIC_LABELS[mk] ?? mk}
                shortLabel={METRIC_SHORT[mk] ?? mk}
                metricKey={mk}
                canEditGoals={canEditGoals}
                onEditGlobalGoal={() => onEditGlobalGoal(mk)}
              />
            ))}
            <Th>Composite</Th>
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <td
                colSpan={data.metric_keys.length + 2}
                className="muted"
                style={{
                  padding: 'var(--sp-4)',
                  textAlign: 'center',
                  fontStyle: 'italic',
                }}
              >
                Nessun consulente.
              </td>
            </tr>
          )}
          {rows.map((row) => {
            const prevRow = prevData?.rows.find(
              (r) => r.user_id === row.user_id,
            )
            const composite = computeComposite(row.metrics)
            return (
              <tr
                key={row.user_id}
                style={{ borderBottom: '1px solid var(--line)' }}
              >
                <td style={cellStyle}>
                  <UserBadge row={row} composite={composite} />
                </td>
                {data.metric_keys.map((mk) => (
                  <td key={mk} style={{ ...cellStyle, padding: 4 }}>
                    <KpiCell
                      cell={row.metrics[mk]}
                      prevCell={prevRow?.metrics[mk]}
                      vizMode={vizMode}
                      canEditGoals={canEditGoals}
                      onDrill={() => onDrill(row, mk)}
                      onEditGoal={() => onEditGoal(row, mk, row.metrics[mk])}
                    />
                  </td>
                ))}
                <td style={{ ...cellStyle, padding: 4, textAlign: 'center' }}>
                  <CompositeCell value={composite} />
                </td>
              </tr>
            )
          })}
          {/* Footer aggregates */}
          {data.team_total && (
            <tr
              style={{
                borderTop: '2px solid var(--line)',
                background: 'var(--bg-2)',
              }}
            >
              <td
                style={{
                  ...cellStyle,
                  fontWeight: 'var(--fw-semibold)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  fontSize: 'var(--fs-10)',
                  color: 'var(--ink-3)',
                }}
              >
                Team totale
              </td>
              {data.metric_keys.map((mk) => {
                const c = data.team_total!.metrics[mk]
                return (
                  <td key={mk} style={{ ...cellStyle, textAlign: 'center' }}>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontWeight: 'var(--fw-semibold)',
                      }}
                    >
                      {c ? c.actual : '—'}
                    </span>
                    {c?.target != null && (
                      <span
                        className="muted"
                        style={{ fontSize: 'var(--fs-10)', marginLeft: 4 }}
                      >
                        / {c.target}
                      </span>
                    )}
                  </td>
                )
              })}
              <td style={cellStyle}></td>
            </tr>
          )}
          {data.team_avg && (
            <tr style={{ background: 'var(--bg-2)' }}>
              <td
                style={{
                  ...cellStyle,
                  fontWeight: 'var(--fw-semibold)',
                  textTransform: 'uppercase',
                  letterSpacing: 'var(--tracking-caps)',
                  fontSize: 'var(--fs-10)',
                  color: 'var(--ink-3)',
                }}
              >
                Team media
              </td>
              {data.metric_keys.map((mk) => {
                const c = data.team_avg!.metrics[mk]
                return (
                  <td key={mk} style={{ ...cellStyle, textAlign: 'center' }}>
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        color: 'var(--ink-2)',
                      }}
                    >
                      {c ? c.actual : '—'}
                    </span>
                  </td>
                )
              })}
              <td style={cellStyle}></td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  )
}

function Th({ children, left }: { children: React.ReactNode; left?: boolean }) {
  return (
    <th
      style={{
        textAlign: left ? 'left' : 'center',
        padding: '10px 12px',
        fontSize: 'var(--fs-10)',
        textTransform: 'uppercase',
        letterSpacing: 'var(--tracking-caps)',
        fontWeight: 'var(--fw-semibold)',
        color: 'var(--ink-3)',
        borderBottom: '1px solid var(--line)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </th>
  )
}

function ThMetric({
  label,
  shortLabel,
  metricKey,
  canEditGoals,
  onEditGlobalGoal,
}: {
  label: string
  shortLabel: string
  metricKey: string
  canEditGoals: boolean
  onEditGlobalGoal: () => void
}) {
  void metricKey
  return (
    <th
      style={{
        textAlign: 'center',
        padding: '8px 8px 6px',
        borderBottom: '1px solid var(--line)',
        whiteSpace: 'nowrap',
        minWidth: 110,
      }}
      title={label}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {canEditGoals && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onEditGlobalGoal()
            }}
            style={{
              background: 'transparent',
              border: 0,
              padding: 0,
              cursor: 'pointer',
              fontSize: 10,
              color: 'var(--ink-4, #9ca3af)',
            }}
            title="Imposta target globale per questa metrica"
          >
            ✎
          </button>
        )}
        <span
          style={{
            fontSize: 'var(--fs-11)',
            color: 'var(--ink-2)',
            fontWeight: 'var(--fw-semibold)',
          }}
        >
          {shortLabel}
        </span>
      </div>
    </th>
  )
}

// ============================================================================
// User badge (avatar + role + chips)
// ============================================================================

function UserBadge({
  row,
  composite,
}: {
  row: UserMetricsRow
  composite: number | null
}) {
  const ini = row.user_name
    .split(/\s+/)
    .map((s) => s[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
  // TOP badge se composite >= 90.
  const isTop = composite != null && composite >= 90
  // Lotti count proxy: somma actual delle metriche aste_vinte+partecipazioni.
  const lottiCount =
    (row.metrics.aste_vinte?.actual ?? 0) +
    (row.metrics.partecipazioni?.actual ?? 0)
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--sp-2)',
        minWidth: 200,
      }}
    >
      <div
        className="avatar md"
        style={{
          background: avatarColor(row.user_id),
          width: 36,
          height: 36,
        }}
      >
        {ini}
      </div>
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontWeight: 'var(--fw-semibold)',
            fontSize: 'var(--fs-13)',
          }}
        >
          {row.user_name}
        </div>
        <div
          className="muted"
          style={{
            fontSize: 'var(--fs-10)',
            display: 'flex',
            gap: 4,
            alignItems: 'center',
            marginTop: 2,
            flexWrap: 'wrap',
          }}
        >
          {isTop && (
            <span
              className="chip accent"
              style={{
                fontSize: 9,
                padding: '1px 6px',
                fontWeight: 'var(--fw-semibold)',
              }}
            >
              ★ TOP
            </span>
          )}
          {lottiCount > 0 && (
            <span
              className="chip outline"
              style={{ fontSize: 9, padding: '1px 6px' }}
            >
              {lottiCount} lott{lottiCount === 1 ? 'o' : 'i'}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Cell con sparkline + multi viz mode
// ============================================================================

function KpiCell({
  cell,
  prevCell,
  vizMode,
  canEditGoals,
  onDrill,
  onEditGoal,
}: {
  cell: MetricCell | undefined
  prevCell: MetricCell | undefined
  vizMode: VizMode
  canEditGoals: boolean
  onDrill: () => void
  onEditGoal: () => void
}) {
  if (!cell) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--ink-4, #9ca3af)' }}>
        —
      </div>
    )
  }
  const pct = cell.progress_pct
  const isOk = pct != null && pct >= 100
  const isBehind = pct != null && pct < 75
  const variantColor = isOk
    ? 'var(--ok-ink, #15803d)'
    : isBehind
      ? 'var(--risk-ink, #b91c1c)'
      : 'var(--ink-2)'
  const variantBg = isOk
    ? 'oklch(95% 0.04 145)'
    : isBehind
      ? 'oklch(96% 0.05 25)'
      : 'transparent'

  let mainValue: string
  let secondaryValue: React.ReactNode = null
  if (vizMode === 'valori') {
    mainValue = String(cell.actual)
    if (cell.target != null) {
      secondaryValue = (
        <span className="muted" style={{ fontSize: 'var(--fs-11)' }}>
          / {cell.target}
        </span>
      )
    }
  } else if (vizMode === 'percento') {
    mainValue = pct != null ? `${Math.round(pct)}%` : '—'
  } else {
    // delta vs prev
    const prev = prevCell?.actual ?? 0
    const diff = cell.actual - prev
    const sign = diff > 0 ? '+' : ''
    mainValue = `${sign}${diff}`
    secondaryValue = (
      <span className="muted" style={{ fontSize: 'var(--fs-10)' }}>
        prev {prev}
      </span>
    )
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        padding: '6px 8px',
        borderRadius: 'var(--r-sm)',
        background: variantBg,
        position: 'relative',
        minHeight: 56,
        cursor: 'pointer',
      }}
      onClick={onDrill}
      title="Click per drill-down eventi"
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--fs-14)',
            fontWeight: 'var(--fw-semibold)',
            color: variantColor,
          }}
        >
          {mainValue}
        </span>
        {secondaryValue}
      </div>
      {/* Sparkline mini: usa events_count fallback random — backend potrebbe
          fornire sequenza daily, oggi solo bar fissa = progress. */}
      <Sparkline pct={pct} isOk={isOk} isBehind={isBehind} />
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: 9,
          color: 'var(--ink-3)',
        }}
      >
        <span>
          {cell.events_count != null && cell.events_count > 0
            ? `${cell.events_count} ev.`
            : ' '}
        </span>
        {canEditGoals && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              onEditGoal()
            }}
            style={{
              background: 'transparent',
              border: 0,
              padding: '0 2px',
              cursor: 'pointer',
              fontSize: 10,
              color: 'var(--accent-ink)',
            }}
            title={cell.target != null ? 'Modifica target user' : 'Imposta target user'}
          >
            🎯
          </button>
        )}
      </div>
    </div>
  )
}

function Sparkline({
  pct,
  isOk,
  isBehind,
}: {
  pct: number | null
  isOk: boolean
  isBehind: boolean
}) {
  // 5 mini bars, height proportional ai % cumulati (placeholder visivo).
  // Backend non fornisce serie daily, simuliamo step progress per dare
  // sensazione di trend visivo coerente con progress_pct.
  if (pct == null) {
    return (
      <div
        style={{
          height: 12,
          display: 'flex',
          gap: 2,
          alignItems: 'flex-end',
        }}
      >
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            style={{
              flex: 1,
              height: 2,
              background: 'var(--bg-3, #e5e7eb)',
              borderRadius: 1,
            }}
          />
        ))}
      </div>
    )
  }
  const color = isOk
    ? 'var(--ok, #16a34a)'
    : isBehind
      ? 'var(--risk, #dc2626)'
      : 'var(--accent, #f97316)'
  // Progressive heights ramp da 30% a pct.
  const max = 12
  const target = Math.min(100, pct)
  const heights = [0.3, 0.5, 0.7, 0.85, 1].map((s) =>
    Math.max(2, Math.round((target * s) / 100 * max)),
  )
  return (
    <div
      style={{
        height: 12,
        display: 'flex',
        gap: 2,
        alignItems: 'flex-end',
      }}
    >
      {heights.map((h, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            height: h,
            background: color,
            opacity: 0.4 + i * 0.15,
            borderRadius: 1,
            transition: 'height 200ms',
          }}
        />
      ))}
    </div>
  )
}

function CompositeCell({ value }: { value: number | null }) {
  if (value == null) {
    return (
      <span className="muted" style={{ fontSize: 'var(--fs-12)' }}>
        —
      </span>
    )
  }
  const variant =
    value >= 90 ? 'ok' : value >= 70 ? 'accent' : value >= 50 ? 'warn' : 'risk'
  const color =
    variant === 'ok'
      ? 'var(--ok-ink, #15803d)'
      : variant === 'accent'
        ? 'var(--accent-ink)'
        : variant === 'warn'
          ? 'oklch(45% 0.15 60)'
          : 'var(--risk-ink, #b91c1c)'
  const bg =
    variant === 'ok'
      ? 'oklch(95% 0.04 145)'
      : variant === 'accent'
        ? 'var(--accent-soft)'
        : variant === 'warn'
          ? 'oklch(96% 0.05 80)'
          : 'oklch(96% 0.05 25)'
  return (
    <div
      style={{
        display: 'inline-flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 2,
        padding: '6px 10px',
        borderRadius: 'var(--r-sm)',
        background: bg,
        minWidth: 70,
      }}
    >
      <span
        style={{
          fontFamily: 'var(--font-mono)',
          fontSize: 'var(--fs-16)',
          fontWeight: 'var(--fw-semibold)',
          color,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 9,
          textTransform: 'uppercase',
          letterSpacing: 'var(--tracking-caps)',
          color,
        }}
      >
        score
      </span>
    </div>
  )
}

// ============================================================================
// Drill modal
// ============================================================================

function DrillModal({
  userId,
  userName,
  metricKey,
  periodId,
  onClose,
}: {
  userId: string
  userName: string
  metricKey: string
  periodId: string
  onClose: () => void
}) {
  const { data, isLoading, isError } = useMetricsEvents(
    {
      user_id: userId,
      metric_key: metricKey,
      period_id: periodId,
      page_size: 50,
    },
    true,
  )
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 640,
          width: '100%',
          maxHeight: '85vh',
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
              {METRIC_LABELS[metricKey] ?? metricKey} · {userName}
            </h2>
            <div
              className="muted"
              style={{ fontSize: 'var(--fs-11)', marginTop: 2 }}
            >
              Eventi periodo {periodId} · {data?.total ?? 0} totali
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        {isLoading && (
          <div className="muted" style={{ fontSize: 'var(--fs-12)' }}>
            Caricamento eventi…
          </div>
        )}
        {isError && <div className="error">Errore caricamento eventi.</div>}
        {!isLoading && data && data.items.length === 0 && (
          <div
            className="muted"
            style={{ fontSize: 'var(--fs-12)', fontStyle: 'italic' }}
          >
            Nessun evento per questa cella nel periodo.
          </div>
        )}
        {!isLoading && data && data.items.length > 0 && (
          <ul
            style={{
              listStyle: 'none',
              padding: 0,
              margin: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-1)',
            }}
          >
            {data.items.map((ev) => (
              <li
                key={ev.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '8px 12px',
                  border: '1px solid var(--line)',
                  borderRadius: 'var(--r-sm)',
                  fontSize: 'var(--fs-12)',
                }}
              >
                <div>
                  <div
                    style={{
                      fontFamily: 'var(--font-mono)',
                      fontSize: 'var(--fs-11)',
                    }}
                  >
                    {fmtDateTime(ev.occurred_at)}
                  </div>
                  <div
                    className="muted"
                    style={{ fontSize: 'var(--fs-10)' }}
                  >
                    {ev.source_type ?? 'event'} ·{' '}
                    {ev.source_id ? ev.source_id.slice(0, 8) : '—'}
                  </div>
                </div>
                <span
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontWeight: 'var(--fw-semibold)',
                    color:
                      ev.delta > 0
                        ? 'var(--ok-ink, #15803d)'
                        : 'var(--risk-ink, #b91c1c)',
                  }}
                >
                  {ev.delta > 0 ? '+' : ''}
                  {ev.delta}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Goal edit modal (per user OR global)
// ============================================================================

function GoalEditModal({
  userId,
  userName,
  metricKey,
  periodId,
  currentTarget,
  goalId,
  rows,
  onClose,
}: {
  userId: string | null
  userName: string
  metricKey: string
  periodId: string
  currentTarget: number | null
  goalId: string | null
  rows: UserMetricsRow[]
  onClose: () => void
}) {
  const [target, setTarget] = useState<string>(
    currentTarget != null ? String(currentTarget) : '',
  )
  const createMut = useCreateMetricGoal()
  const patchMut = usePatchMetricGoal()
  const pending = createMut.isPending || patchMut.isPending
  const error = createMut.error?.message ?? patchMut.error?.message ?? null

  function handleSave() {
    const t = Number(target)
    if (!Number.isFinite(t) || t < 0) return
    if (userId == null) {
      // Global: create goal per ogni user.
      Promise.all(
        rows.map((r) =>
          createMut.mutateAsync({
            user_id: r.user_id,
            metric_key: metricKey,
            period_id: periodId,
            target: t,
          }),
        ),
      )
        .then(() => onClose())
        .catch(() => {
          /* errore mostrato sotto */
        })
      return
    }
    if (goalId) {
      patchMut.mutate(
        { goalId, body: { target: t } },
        { onSuccess: () => onClose() },
      )
    } else {
      createMut.mutate(
        {
          user_id: userId,
          metric_key: metricKey,
          period_id: periodId,
          target: t,
        },
        { onSuccess: () => onClose() },
      )
    }
  }

  const isGlobal = userId == null
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 'var(--sp-3)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface)',
          borderRadius: 'var(--r-md)',
          padding: 'var(--sp-4)',
          maxWidth: 460,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
          }}
        >
          <h2 style={{ margin: 0, fontSize: 'var(--fs-16)' }}>
            🎯 {goalId ? 'Modifica' : 'Imposta'} target
            {isGlobal && ' globale'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 0,
              fontSize: 18,
              cursor: 'pointer',
            }}
          >
            ×
          </button>
        </div>
        <p className="muted" style={{ margin: 0, fontSize: 'var(--fs-12)' }}>
          {METRIC_LABELS[metricKey] ?? metricKey} · {userName} · {periodId}
        </p>
        {isGlobal && (
          <p
            className="muted"
            style={{
              margin: 0,
              fontSize: 'var(--fs-11)',
              padding: '6px 10px',
              background: 'var(--accent-soft)',
              borderRadius: 'var(--r-sm)',
              color: 'var(--accent-ink)',
            }}
          >
            ⚠ Crea goal con stesso target per <b>{rows.length}</b> consulenti.
            Sovrascrive eventuali goal user-specific esistenti.
          </p>
        )}
        <div className="form-field">
          <label>Target (numero)</label>
          <input
            type="number"
            min={0}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            autoFocus
          />
        </div>
        {error && (
          <div className="error" style={{ fontSize: 'var(--fs-12)' }}>
            {error}
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: 'var(--sp-2)',
            justifyContent: 'flex-end',
          }}
        >
          <button
            type="button"
            className="btn secondary"
            onClick={onClose}
            disabled={pending}
          >
            Annulla
          </button>
          <button
            type="button"
            className="btn accent"
            onClick={handleSave}
            disabled={pending || target === ''}
          >
            {pending ? 'Salvataggio…' : 'Salva'}
          </button>
        </div>
      </div>
    </div>
  )
}

const cellStyle: React.CSSProperties = {
  padding: '8px 10px',
  verticalAlign: 'middle',
}

function avatarColor(seed: string): string {
  let h = 0
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) | 0
  const hue = Math.abs(h) % 360
  return `oklch(58% 0.14 ${hue})`
}
